import React from "react";
import { Loader2, Star } from "lucide-react";
import { apiKirimUlasan } from "@/lib/publicApi";
import { BINTANG_TEKS, gantiTanda, MAKS_CATATAN, siapKirim } from "@/lib/ulasan";

// Owner 2026-10-16 (requirement 60): one rating per order on the customer's
// digital nota, after the order was picked up / delivered. The note is plain
// text (React escapes it; links are never clickable). `situs` is a hidden
// honeypot field a person never fills; the server also wants a few seconds on the page.
const Bintang = ({ nilai, besar = false }) => (
  <span className="inline-flex" aria-label={`${nilai} dari 5 bintang`}>
    {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={besar ? 26 : 16} className={i <= nilai ? "fill-amber-400 text-amber-400" : "text-slate-300"} />)}
  </span>
);

export default function UlasanNota({ token, ulasan }) {
  const [u, setU] = React.useState(ulasan);
  const [bintang, setBintang] = React.useState(null);
  const [tanda, setTanda] = React.useState([]);
  const [catatan, setCatatan] = React.useState("");
  const [situs, setSitus] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const dibuka = React.useRef(Date.now());
  if (!u || (!u.bisa && !u.terkirim)) return null;

  if (u.terkirim) {
    const t = u.terkirim;
    return (
      <section data-testid="ulasan-terkirim" className="my-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm print:hidden">
        <div className="font-semibold text-emerald-800">Terima kasih atas penilaian Anda</div>
        <div className="mt-1"><Bintang nilai={t.bintang} /></div>
        {t.tanda.length > 0 && <div className="mt-1 text-slate-700">{t.tanda.join(" · ")}</div>}
        {t.catatan && <p className="mt-1 whitespace-pre-wrap break-words text-slate-700">{t.catatan}</p>}
      </section>
    );
  }

  const kirim = async () => {
    setBusy(true); setError(null);
    try {
      const r = await apiKirimUlasan(token, { bintang, tanda, catatan: catatan.trim() || null, situs: situs || null,
        detik_di_halaman: Math.floor((Date.now() - dibuka.current) / 1000) });
      setU({ bisa: false, terkirim: r.terkirim });
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Penilaian belum terkirim. Coba lagi.");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="ulasan-form" className="my-3 rounded-lg border-2 border-amber-400 bg-amber-50/60 p-3 text-sm print:hidden">
      <div className="font-semibold text-soq-biru-tua">Beri penilaian pesanan ini</div>
      <p className="text-xs text-slate-600">Penilaian Anda hanya dibaca pemilik toko, tidak ditampilkan ke orang lain.</p>
      <div className="mt-2 flex items-center gap-1" role="radiogroup" aria-label="Bintang">
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" data-testid={`ulasan-bintang-${i}`} role="radio" aria-checked={bintang === i} onClick={() => setBintang(i)}
            className="min-h-[44px] min-w-[44px] rounded-lg p-1 hover:bg-amber-100" title={BINTANG_TEKS[i]}>
            <Star size={30} className={bintang && i <= bintang ? "fill-amber-400 text-amber-500" : "text-slate-400"} />
          </button>
        ))}
        <span data-testid="ulasan-bintang-teks" className="ml-1 text-sm font-semibold text-slate-700">{bintang ? BINTANG_TEKS[bintang] : ""}</span>
      </div>
      <div data-testid="ulasan-tanda" className="mt-2 flex flex-wrap gap-1.5">
        {Object.entries(u.tanda_pilihan || {}).map(([k, label]) => (
          <button key={k} type="button" data-testid={`ulasan-tanda-${k}`} aria-pressed={tanda.includes(k)} onClick={() => setTanda(gantiTanda(tanda, k))}
            className={`min-h-[36px] rounded-full border px-3 text-xs ${tanda.includes(k) ? "border-soq-biru bg-soq-biru text-white" : "border-slate-300 bg-white text-slate-700"}`}>{label}</button>
        ))}
      </div>
      <label className="mt-2 block text-xs text-slate-600">Catatan (boleh kosong, maks {MAKS_CATATAN} huruf)
        <textarea data-testid="ulasan-catatan" rows={3} maxLength={MAKS_CATATAN} value={catatan} onChange={(e) => setCatatan(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900" /></label>
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={situs} onChange={(e) => setSitus(e.target.value)}
        name="situs" className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      {error && <p data-testid="ulasan-error" className="mt-2 text-sm text-red-600">{error}</p>}
      <button type="button" data-testid="ulasan-kirim" disabled={busy || !siapKirim({ bintang, catatan })} onClick={kirim}
        className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-soq-oranye px-4 text-sm font-semibold text-soq-biru-tua disabled:opacity-40">
        {busy && <Loader2 size={15} className="animate-spin" />} Kirim penilaian</button>
    </section>
  );
}
